import { useState, useRef, useCallback } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  Typography,
  Popper,
  ClickAwayListener,
  makeStyles,
} from '@material-ui/core';
import InputField from '../inputField';
import CustomButton from '../customButton';
import CustomCheckbox from '../customCheckbox';
import VirtualizedList from '../virtualizedList';
import VirtualSuggestion from './virtualSuggestion';
import VirtualSelectedUser from './virtualSelectedUser';
import { isDirty, setPopperWidth, getScrollbarHeight } from './config';
import {
  isArrayEmpty,
  isItemInList,
  getUnionOfTwoArrays,
  isArraySubset,
  trimString,
  getAssociates,
  getEmployees,
} from '../../../utility/helpers';
import { useCustomEffect, useElementMaxHeight } from '../../../utility/hooks';
import {
  customSearch,
  sortPeopleAlphabetically,
} from '../../../utility/uiUtils';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  box: {
    minWidth: 'fit-content',
  },
  picker: {
    display: 'flex',
    width: '100%',
    position: 'relative',
  },
  popperRoot: {
    boxShadow: 'none',
    borderRadius: 4,
    zIndex: 1,
  },
  popperMain: {
    boxSizing: 'border-box',
    backgroundColor: primary.white,
    border: `1px solid ${primary.bluish6}`,
    height: 307,
    borderRadius: 4,
    padding: spacing(4, 0),
    marginTop: spacing(1),
  },
  popperContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  popoverFooter: {
    display: 'flex',
    marginTop: spacing(4),
    paddingTop: spacing(4),
    borderTop: `1px solid ${primary.bluish7}`,
  },
  selectAllItem: {
    margin: spacing(0, 4),
    padding: spacing(2),
  },
  checkboxContainer: {
    display: 'flex',
    flexShrink: 0,
    width: 24,
    height: 24,
  },
  noResults: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: spacing(22.5),
  },
  noResultsWithSelectAllEmployees: {
    marginTop: spacing(17.5),
  },
  noResultsWithSelectAll: {
    marginTop: spacing(12.5),
  },
  hover: {
    transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    '&:hover': {
      backgroundColor: primary.bluish8,
    },
  },
  selectButton: {
    marginRight: spacing(4),
    marginLeft: 'auto',
  },
  selectedUserWrapper: {
    borderBottom: `1px solid ${primary.bluish7}`,
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  selectAllWrapper: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  selectAllTitle: {
    color: primary.bluish2,
    marginLeft: spacing(2),
    flexGrow: 1,
    userSelect: 'none',
  },
  disabledSelectAll: {
    cursor: 'default',
    '&:hover': {
      backgroundColor: primary.white,
    },
  },
  suggestionsWrapper: {
    height: ({ suggestionsWrapperHeight }) => suggestionsWrapperHeight,
  },
  suggestionsViewport: {
    maxHeight: ({ suggestionsWrapperHeight }) => suggestionsWrapperHeight,
  },
  suggestionsScrollY: {
    '& .os-scrollbar-vertical': {
      padding: spacing(2.75, 0),
      right: 5,
    },
  },
  selectedUsersViewport: {
    maxHeight: ({ shouldCalculateSelectionHeight, selectionMaxHeight }) =>
      shouldCalculateSelectionHeight ? selectionMaxHeight : 528,
  },
  selectedUsersWrapper: {
    marginTop: spacing(6),
    flex: 1,
  },
  selectedUsersScrollY: {
    right: -15,
  },
}));

const PeoplePicker = ({
  parentNode,
  className = undefined,
  selectedUsersScrollYClass = undefined,
  translations,
  options = [],
  preselectedUsers = [],
  blacklistedUsers = [],
  formattedUsers,
  parentOffset = 0,
  isDisabled = false,
  hasError = false,
  errorMessage = '',
  showSelection = false,
  showDirectReports = false,
  hasSelectAll = false,
  dropdownWidth = null,
  shouldSortAlphabetically = false,
  shouldCalculateSelectionHeight = false,
  onSelect,
  shouldHandleTypeLabel = false,
  handleTypeLabel = () => {},
}) => {
  const inputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState(options);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(preselectedUsers);
  const [setElementRef, selectionMaxHeight] = useElementMaxHeight(
    parentNode,
    !hasError &&
      showSelection &&
      shouldCalculateSelectionHeight &&
      !isArrayEmpty(preselectedUsers),
    parentOffset
  );
  const allEmployees = getEmployees(options);
  const allAssociates = getAssociates(options);
  const hasUsers = !isArrayEmpty(users);
  const hasEmployees = !isArrayEmpty(allEmployees);
  const hasAssociates = !isArrayEmpty(allAssociates);
  const suggestionsWrapperHeight = getScrollbarHeight(
    hasSelectAll && hasUsers,
    hasAssociates,
    hasEmployees
  );
  const classes = useStyles({
    suggestionsWrapperHeight,
    selectionMaxHeight,
    shouldCalculateSelectionHeight,
  });

  const disabledEmployeesCount = allEmployees.filter(
    user => user.isDisabled
  ).length;
  const isSelectAllEmployeesDisabled =
    disabledEmployeesCount === allEmployees.length;

  const calculateSuggestionItemSize = useCallback(
    userIndex => {
      const user = users?.[userIndex];
      const reportsArray = formattedUsers?.[user?.id];
      const directReports = reportsArray
        ? reportsArray.filter(
            reporter => !blacklistedUsers.includes(reporter.id)
          )
        : [];

      return showDirectReports && !isArrayEmpty(directReports) ? 84 : 40;
    },
    [users, blacklistedUsers, formattedUsers, showDirectReports]
  );

  const getPreselectedVirtualUserSize = useCallback(
    index => (index === preselectedUsers.length - 1 ? 24 : 40),
    [preselectedUsers]
  );

  const handleClose =
    (shouldResetSelected = false) =>
    () => {
      setIsOpen(false);
      setSearch('');
      setUsers([]);
      setIsFocused(false);

      if (shouldResetSelected) {
        setSelectedUsers(preselectedUsers);
      }
    };

  const onSetAnchorEl = () => setAnchorEl(inputRef?.current);

  const handleOpen = () => {
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const getPeople = (term = '') => {
    const searchTerm = trimString(term);
    const newUsers = customSearch(options, searchTerm, true);

    return setUsers(newUsers);
  };

  const handleChange = event => {
    const { value } = event.target;

    setSearch(value);
    handleOpen();
    return getPeople(value);
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    setIsOpen(true);
  };

  const handleSelectAll = allOptions => () => {
    if (isArraySubset(selectedUsers, allOptions)) {
      setSelectedUsers(prevSelectedUsers =>
        prevSelectedUsers.filter(
          sUser => !allOptions.some(u => u.id === sUser.id && !u.isDisabled)
        )
      );
    } else {
      setSelectedUsers(prevSelectedUsers =>
        getUnionOfTwoArrays(prevSelectedUsers, allOptions)
      );
    }
  };

  const handleUserSelect = useCallback(
    currentUser => {
      const isSelected = isItemInList(selectedUsers, currentUser);

      if (isSelected) {
        setSelectedUsers(prevSelectedUsers =>
          prevSelectedUsers.filter(u => u.id !== currentUser.id)
        );
      } else {
        setSelectedUsers(prevSelectedUsers => [
          ...prevSelectedUsers,
          currentUser,
        ]);
      }
    },
    [selectedUsers]
  );

  const handleDirectReportSelect = useCallback(
    reporters => {
      const isSelected = isArraySubset(selectedUsers, reporters);

      if (isSelected) {
        setSelectedUsers(prevSelectedUsers =>
          prevSelectedUsers.filter(user => !isItemInList(reporters, user))
        );
      } else {
        setSelectedUsers(prevSelectedUsers =>
          getUnionOfTwoArrays(prevSelectedUsers, reporters)
        );
      }
    },
    [selectedUsers]
  );

  const handleDeselectUser = useCallback(
    currentUser => {
      const selectedUsersUpdated = selectedUsers.filter(
        u => u.id !== currentUser.id
      );

      setSelectedUsers(selectedUsersUpdated);
      onSelect(selectedUsersUpdated);
    },
    [selectedUsers, onSelect]
  );

  const handleAddUsers = () => {
    const shouldAddUsers = isDirty(preselectedUsers, selectedUsers);

    if (shouldAddUsers) {
      onSelect(
        shouldSortAlphabetically
          ? sortPeopleAlphabetically(selectedUsers)
          : selectedUsers
      );
    }

    handleClose()();
  };

  useCustomEffect(onSetAnchorEl);

  useCustomEffect(
    () => {
      if (!isArrayEmpty(preselectedUsers)) {
        setSelectedUsers(preselectedUsers);
      }
      if (isFocused) {
        if (isArrayEmpty(preselectedUsers)) {
          setSelectedUsers([]);
        }
        getPeople();
      }
    },
    [isFocused, preselectedUsers],
    false
  );

  const renderSuggestion = useCallback(
    user => {
      const isUserSelected = isItemInList(selectedUsers, user);
      const reportsArray = formattedUsers[user.id];
      const directReports = reportsArray
        ? reportsArray.filter(
            reporter => !blacklistedUsers.includes(reporter.id)
          )
        : [];
      const isDirectReportsSelected = isArrayEmpty(directReports)
        ? false
        : isArraySubset(selectedUsers, directReports);

      return (
        <VirtualSuggestion
          translations={translations}
          user={user}
          directReports={directReports}
          isUserSelected={isUserSelected}
          isDirectReportsSelected={isDirectReportsSelected}
          showDirectReports={showDirectReports}
          onChange={handleUserSelect}
          onDirectReportsChange={handleDirectReportSelect}
        />
      );
    },
    [
      translations,
      blacklistedUsers,
      formattedUsers,
      selectedUsers,
      showDirectReports,
      handleDirectReportSelect,
      handleUserSelect,
    ]
  );

  const renderSelectedUser = useCallback(
    user => {
      const typeLabel = shouldHandleTypeLabel ? handleTypeLabel(user) : false;

      return (
        <VirtualSelectedUser
          translations={translations}
          user={user}
          typeLabel={typeLabel || ''}
          shouldHandleTypeLabel={shouldHandleTypeLabel}
          onDeselect={handleDeselectUser}
        />
      );
    },
    [translations, handleTypeLabel, shouldHandleTypeLabel, handleDeselectUser]
  );

  return (
    <div className={className}>
      <ClickAwayListener
        onClickAway={handleClose(true)}
        mouseEvent="onMouseDown"
      >
        <div className={classes.picker}>
          <InputField
            ref={inputRef}
            label={translations.label}
            placeholder={translations.placeholder}
            value={search}
            error={hasError}
            errorMessage={errorMessage}
            disabled={isDisabled}
            onChange={handleChange}
            onInputFocus={handleInputFocus}
            autoComplete="off"
            fullWidth
          />
          <Popper
            className={classes.popperRoot}
            style={{
              width: anchorEl ? anchorEl.clientWidth : null,
            }}
            anchorEl={anchorEl}
            open={isOpen}
            modifiers={{
              offset: { enabled: true, offset: '-12, 0' },
              preventOverflow: {
                enabled: true,
                escapeWithReference: true,
                boundariesElement: 'viewport',
              },
            }}
            popperOptions={{
              onCreate: ({ instance: { reference, popper } }) =>
                setPopperWidth(reference, popper, dropdownWidth),
              onUpdate: ({ instance: { reference, popper } }) =>
                setPopperWidth(reference, popper, dropdownWidth),
            }}
            placement="bottom-start"
            disablePortal
          >
            <div className={classes.popperMain}>
              {hasSelectAll && hasUsers && (
                <>
                  {!isArrayEmpty(allEmployees) && (
                    <div
                      className={classNames(
                        classes.selectAllItem,
                        classes.hover,
                        classes.selectAllWrapper,
                        {
                          [classes.disabledSelectAll]:
                            isSelectAllEmployeesDisabled,
                        }
                      )}
                      onClick={
                        isSelectAllEmployeesDisabled
                          ? () => {}
                          : handleSelectAll(allEmployees)
                      }
                    >
                      <Typography className={classes.selectAllTitle}>
                        {translations.allEmployees}
                      </Typography>
                      <div className={classes.checkboxContainer}>
                        <CustomCheckbox
                          isChecked={
                            !isArrayEmpty(options) &&
                            isArraySubset(selectedUsers, allEmployees)
                          }
                          disabled={isSelectAllEmployeesDisabled}
                          onChange={handleSelectAll(allEmployees)}
                          isControlled
                        />
                      </div>
                    </div>
                  )}
                  {!isArrayEmpty(allAssociates) && (
                    <div
                      className={classNames(
                        classes.selectAllItem,
                        classes.hover,
                        classes.selectAllWrapper
                      )}
                      onClick={handleSelectAll(allAssociates)}
                    >
                      <Typography className={classes.selectAllTitle}>
                        {translations.allAssociates}
                      </Typography>
                      <div className={classes.checkboxContainer}>
                        <CustomCheckbox
                          isChecked={
                            !isArrayEmpty(options) &&
                            isArraySubset(selectedUsers, allAssociates)
                          }
                          onChange={handleSelectAll(allAssociates)}
                          isControlled
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
              <div className={classes.popperContent}>
                <div className={classes.suggestionsWrapper}>
                  {hasUsers ? (
                    <VirtualizedList
                      verticalScrollClass={classes.suggestionsScrollY}
                      viewportClass={classes.suggestionsViewport}
                      items={users}
                      count={users?.length ?? 0}
                      estimateSize={calculateSuggestionItemSize}
                      renderItem={renderSuggestion}
                    />
                  ) : (
                    <Typography
                      className={classNames(classes.noResults, {
                        [classes.noResultsWithSelectAllEmployees]:
                          hasUsers &&
                          hasSelectAll &&
                          (hasEmployees || hasAssociates),
                        [classes.noResultsWithSelectAll]:
                          hasUsers &&
                          hasSelectAll &&
                          hasEmployees &&
                          hasAssociates,
                      })}
                    >
                      {translations.noUserResults}
                    </Typography>
                  )}
                </div>
              </div>
              {hasUsers || hasSelectAll ? (
                <div className={classes.popoverFooter}>
                  <CustomButton
                    className={classes.selectButton}
                    type="withTextLightRounded"
                    onClick={handleAddUsers}
                  >
                    {translations.selectButtonLabel}
                  </CustomButton>
                </div>
              ) : null}
            </div>
          </Popper>
        </div>
      </ClickAwayListener>
      {showSelection && !isArrayEmpty(preselectedUsers) && (
        <div ref={setElementRef} className={classes.selectedUsersWrapper}>
          <VirtualizedList
            viewportClass={classes.selectedUsersViewport}
            verticalScrollClass={classNames(
              classes.selectedUsersScrollY,
              selectedUsersScrollYClass
            )}
            itemClass={classes.selectedUserWrapper}
            items={preselectedUsers}
            count={preselectedUsers?.length ?? 0}
            gap={16}
            estimateSize={getPreselectedVirtualUserSize}
            renderItem={renderSelectedUser}
          />
        </div>
      )}
    </div>
  );
};

PeoplePicker.propTypes = {
  className: PropTypes.string,
  selectedUsersScrollYClass: PropTypes.string,
  translations: PropTypes.object.isRequired,
  preselectedUsers: PropTypes.arrayOf(PropTypes.shape({})),
  blacklistedUsers: PropTypes.arrayOf(PropTypes.number),
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
  dropdownWidth: PropTypes.string,
  parentOffset: PropTypes.number,
  isDisabled: PropTypes.bool,
  hasSelectAll: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  showDirectReports: PropTypes.bool,
  shouldSortAlphabetically: PropTypes.bool,
  showSelection: PropTypes.bool,
  shouldCalculateSelectionHeight: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  shouldHandleTypeLabel: PropTypes.bool,
  handleTypeLabel: PropTypes.func,
};

export default PeoplePicker;
